/* eslint-disable jsx-a11y/anchor-is-valid */
import {useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useNavigate, useParams} from 'react-router-dom'
import {PageLink, PageTitle} from '../../../../_metronic/layout/core/PageData'
import { DropdownMenu, SettingModalOption, SettingOption } from '../../../common/component/DropdownMenu'
import { CustomInputRenderer } from '../../../common/component/CustomInputRenderer'
import { SubSectionHeader } from '../../../common/component/SubSectionHeader'
import { DeleteConfirmationModal } from '../../../common/component/modal/DeleteConfirmationModal'
import { MessageToast, MessageType } from '../../../common/component/MessageToast'
import { Toast } from 'bootstrap'
import { parseLocalDate } from '../../../util/Locale'
import { renderIdentifier } from '../../../util/Constants'
import { Spinner } from '../../../common/component/Spinner'
import {useContactDeleteByIdMutation, useContactGetByIdMutation} from '../Contact.api'
import { IContactDto } from '../Contact.model'

export const ContactDetailsPage = () => {
  const intl = useIntl()
  const navigate = useNavigate()
  const {contactId} = useParams()
  const breadCrumbs: Array<PageLink> = [
    {
      title: intl.formatMessage({id: 'BREADCRUMBS.DIRECTORY'}),
      path: '/contacts',
      isSeparator: false,
      isActive: false,
    },
  ]
  const settingOptions: SettingOption[] = [
    {
      label: intl.formatMessage({id: 'UTIL.EDIT'}),
      icon: 'bi-pencil',
      action: () => navigate(`/contacts/${contactId}/edit`),
    },
  ]
  const removeSettingOption: SettingModalOption = {
    label: intl.formatMessage({id: 'UTIL.DELETE'}),
    targetModal: '#contact_delete_confirmation_modal',
  }

  const didFetchData = useRef(false)
  const didRequestDelete = useRef(false)

  const [loadError, setLoadError] = useState<string | undefined>(undefined)
  const [toastEntry, setToastEntry] = useState<[MessageType, string]>()

  const [openDropDown, setOpenDropDown] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [contact, setContact] = useState<IContactDto>()

  const [getById] = useContactGetByIdMutation()
  const [deleteById] = useContactDeleteByIdMutation()

  useEffect(() => {
    const fetchData = async (id) => {
      try {
        if (!didFetchData.current) {
          setIsLoading(true)
          const response: any = await getById({id})
          if (!response || response.error) {
            setLoadError(intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'}))
          } else {
            setContact(response.data)
          }
        }
      } catch (e: any) {
        setLoadError(intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'}))
      } finally {
        setIsLoading(false)
      }
    }

    if (contactId) {
      fetchData(contactId)
    }
  }, [])

  useEffect(() => {
    if (toastEntry) {
      const [messageType] = toastEntry
      const toastElement = document.getElementById('#CustomToast')
      const delay =
        messageType === MessageType.ERROR || messageType === MessageType.WARNING ? 10000 : 2000
      if (toastElement) {
        toastElement.addEventListener('hidden.bs.toast', () => setToastEntry(undefined))
        const toast = new Toast(toastElement, {autohide: true, delay})
        toast.show()
      }
    }
  }, [toastEntry])

  const deleteConfirmOnClickHandler = async () => {
    try {
      if (contactId && !didRequestDelete.current) {
        setIsLoading(true)
        const response: any = await deleteById(contactId)
        if (!response || response.error) {
          setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
        } else {
          navigate(`/contacts`)
        }
      }
    } catch (e) {
      setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      {toastEntry && <MessageToast messageType={toastEntry[0]} message={toastEntry[1]} />}
      <PageTitle breadcrumbs={breadCrumbs}></PageTitle>
      <div className='card card-flush card-stretch-100 card-bordered mt-5'>
        <div className='card-body'>
          {loadError && (
            <div className='alert alert-danger d-flex align-items-center px-5 py-2 mb-10'>
              <span className='svg-icon svg-icon-2hx svg-icon-primary ms-3 me-5'>
                <i className='bi bi-database-exclamation fs-2'></i>
              </span>

              <div className='d-flex flex-column'>
                <span>{loadError}</span>
              </div>

              <button
                type='button'
                className='position-absolute position-sm-relative m-2 m-sm-0 top-0 end-0 btn btn-icon ms-sm-auto'
                data-bs-dismiss='alert'
              >
                <div className='badge px-3 py-2'>
                  <span className='text-danger fs-1'>&times;</span>
                </div>
              </button>
            </div>
          )}

          <div className='d-flex flex-row'>
            <div className='d-flex flex-column flex-row-fluid'>
              <div className='d-flex flex-row flex-column-fluid'>
                <div className='d-flex flex-row-fluid flex-start'>
                  {contact && (
                    <div className='d-flex flex-column'>
                      <span className='text-gray-800 fs-2 fw-bolder'>{contact.displayName}</span>
                      <span className='fw-bolder text-primary me-1 fs-5'>
                        {renderIdentifier(contact.sequence)}
                      </span>
                    </div>
                  )}
                </div>

                <div className='d-flex flex-row-auto flex-start ms-5'>
                  {contact && !contact.userId && (
                    <>
                      <button
                        className='btn btn-sm btn-light-primary me-2'
                        onClick={() => setOpenDropDown((prev) => !prev)}
                        disabled={isLoading}
                      >
                        <i className='bi bi-three-dots fs-5'></i>
                      </button>
                      <DropdownMenu
                        visible={openDropDown}
                        setOpenDropDown={setOpenDropDown}
                        settingOptions={settingOptions}
                        deleteOption={removeSettingOption}
                      ></DropdownMenu>
                    </>
                  )}

                  <button className='btn btn-sm btn-light' onClick={() => navigate(`/contacts`)}>
                    <i className='bi bi-x-lg'></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {isLoading ? (
            <Spinner />
          ) : !contact ? (
            <div className='row'>
              <div className='d-flex flex-row-fluid flex-start mt-10'>
                <span className='text-danger '>
                  <div className='d-flex flex-row justify-content-start align-items-center'>
                    <span className='fs-6 me-1'>
                      <i className='text-danger me-1 bi bi-exclamation-octagon' />
                      {intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'})}
                    </span>
                  </div>
                </span>
              </div>
            </div>
          ) : (
            <>
              <div>
                <ul className='nav nav-tabs nav-line-tabs mb-5 fs-6 mt-5'>
                  <li className='nav-item'>
                    <a className='nav-link active' data-bs-toggle='tab' href='#kt_tab_pane_1'>
                      {intl.formatMessage({id: 'UTIL.INFORMATION'})}
                    </a>
                  </li>
                </ul>
                <div className='tab-content mt-10' id='myTabContent'>
                  <div className='tab-pane fade active show' id='kt_tab_pane_1' role='tabpanel'>
                    <div className='row mb-5'>
                      <SubSectionHeader
                        title={intl.formatMessage({id: 'UTIL.GENERAL'})}
                        separator={false}
                      />

                      <div className='px-5'>
                        <div className='row mb-5'>
                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.CREATED_DATE'})}
                            value={parseLocalDate(
                              contact.createdAt,
                              intl.formatMessage({id: 'I18N.LOCALE'})
                            )}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.UPDATED_DATE'})}
                            value={parseLocalDate(
                              contact.updatedAt,
                              intl.formatMessage({id: 'I18N.LOCALE'})
                            )}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.ID'})}
                            value={`${contact.sequence}`}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.DISPLAY_NAME'})}
                            value={contact.displayName}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.EMAIL'})}
                            value={`${contact.email ?? ''}`}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.TELEPHONE'})}
                            value={`${contact.telephone ?? ''}`}
                          ></CustomInputRenderer>
                        </div>
                      </div>
                    </div>

                    {contact.details && contact.details.length > 0 && (
                      <>
                        <SubSectionHeader
                          title={intl.formatMessage({id: 'UTIL.ADDITIONAL_DETAILS'})}
                        />

                        <div className='px-5'>
                          <div className='row mb-5'>
                            {contact.details &&
                              contact.details.map((detail, index) => (
                                <div key={index}>
                                  <CustomInputRenderer
                                    label={intl.formatMessage({id: `UTIL.${detail.type}`})}
                                    value={`${detail.value ?? ''}`}
                                  ></CustomInputRenderer>
                                </div>
                              ))}
                          </div>
                        </div>
                      </>
                    )}

                    <div className='row mb-5'>
                      <SubSectionHeader title={intl.formatMessage({id: 'UTIL.LOCATION'})} />

                      <div className='px-5'>
                        <div className='row mb-5'>
                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.COUNTRY'})}
                            value={
                              contact.countryCode
                                ? intl.formatMessage({
                                    id: `DROPDOWN.COUNTRY.${contact.countryCode}`,
                                  })
                                : ''
                            }
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.ADDRESS'})}
                            value={contact.address}
                          ></CustomInputRenderer>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <DeleteConfirmationModal
                id='contact_delete_confirmation_modal'
                title={intl
                  .formatMessage({id: 'UTIL.DELETE_CONFIRMATION.TITLE'})
                  .replace(/%s/g, intl.formatMessage({id: 'UTIL.CONTACT'}))}
                description={`${contact?.displayName} - #${contact?.sequence}`}
                confirmOnClickHandler={deleteConfirmOnClickHandler}
              ></DeleteConfirmationModal>
            </>
          )}
        </div>
      </div>
    </>
  )
}
